
import { useEffect, useState } from 'react';
import { type CalendarDay, type DailySchedule, type ScheduledTask, type TaskStats, type TaskStatus } from '../types/tasks';
import { tasksService } from '../services/tasks';
import { ProgressBar, StatusBadge, TaskCalendar, TaskCard } from '../components/molecules';
import { Button } from '../components/atoms';

export function TasksPage() {
  const [schedule, setSchedule] = useState<DailySchedule | null>(null);
  const [weeklyProgress, setWeeklyProgress] = useState<CalendarDay[]>([]);
  const [taskStats, setTaskStats] = useState<TaskStats | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [selectedTask, setSelectedTask] = useState<ScheduledTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'overview' | 'calendar' | 'details'>('overview');

  useEffect(() => {
    void fetchTasksData();
  }, [selectedDate]);

  const fetchTasksData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [scheduleData, weeklyData, statsData] = await Promise.allSettled([
        tasksService.getDailySchedule(selectedDate),
        tasksService.getWeeklyProgress(),
        tasksService.getTaskStats()
      ]);

      if (scheduleData.status === 'fulfilled') {
        setSchedule(scheduleData.value);
      } else {
        console.warn('Failed to fetch schedule:', scheduleData.reason);
      }

      if (weeklyData.status === 'fulfilled') {
        setWeeklyProgress(weeklyData.value);
      } else {
        console.warn('Failed to fetch weekly progress:', weeklyData.reason);
      }

      if (statsData.status === 'fulfilled') {
        setTaskStats(statsData.value);
      } else {
        console.warn('Failed to fetch task stats:', statsData.reason);
      }

    } catch (err) {
      console.error('Failed to fetch tasks data:', err);
      setError('Unable to load tasks data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleTaskClick = (task: ScheduledTask) => {
    setSelectedTask(task);
    setView('details');
  };

  const handleDayClick = (day: CalendarDay) => {
    setSelectedDate(day.date);
    setView('overview');
  };

  const handleBackToOverview = () => {
    setSelectedTask(null);
    setView('overview');
  };

  const getAllTasks = (): ScheduledTask[] => {
    if (!schedule) {return [];}
    
    const tasks: ScheduledTask[] = [];
    schedule.sections.forEach(section => {
      if (section.tasks) {
        tasks.push(...section.tasks);
      }
    });

    return tasksService.sortTasksByTime(tasks);
  };

  const getTasksByStatus = (status: TaskStatus): ScheduledTask[] => getAllTasks().filter(task => task.status === status);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your tasks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={fetchTasksData}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (view === 'details' && selectedTask) {
    return <TaskDetailsView task={selectedTask} onBack={handleBackToOverview} />;
  }

  if (view === 'calendar') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Task Calendar</h1>
                <p className="text-gray-600 mt-1">View your task completion over time</p>
              </div>
              <Button onClick={() => setView('overview')} variant="secondary">
                ← Back to Tasks
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-8">
          <TaskCalendar
            days={weeklyProgress}
            variant="week"
            onDayClick={handleDayClick}
            className="mb-8"
          />
          
          {taskStats && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistics</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{taskStats.totalTasks}</div>
                  <div className="text-sm text-gray-600">Total Tasks</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{taskStats.completedTasks}</div>
                  <div className="text-sm text-gray-600">Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">{taskStats.completionRate}%</div>
                  <div className="text-sm text-gray-600">Completion Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{taskStats.streak}</div>
                  <div className="text-sm text-gray-600">Day Streak</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Daily Tasks
              </h1>
              <p className="text-gray-600 mt-1">
                {selectedDate === 'today' 
                  ? 'Today\'s schedule and progress' 
                  : `Tasks for ${new Date(selectedDate).toLocaleDateString()}`
                }
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                onClick={() => setView('calendar')} 
                variant="secondary"
              >
                📅 Calendar View
              </Button>
              <Button onClick={fetchTasksData}>
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Overview Cards */}
        {schedule && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Overall Progress</h3>
                  <div className="text-3xl font-bold text-blue-600 mt-2">
                    {schedule.overallProgress}%
                  </div>
                </div>
                <div className="text-blue-500 text-4xl">📊</div>
              </div>
              <ProgressBar
                progress={schedule.overallProgress}
                variant={schedule.overallProgress >= 80 ? 'success' : 'default'}
                className="mt-4"
                animated
              />
              <p className="text-sm text-gray-600 mt-2">
                {schedule.completedTasks} of {schedule.totalTasks} tasks completed
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Completed</h3>
                  <div className="text-3xl font-bold text-green-600 mt-2">
                    {getTasksByStatus('completed').length}
                  </div>
                </div>
                <div className="text-green-500 text-4xl">✅</div>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                Tasks finished today
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Pending</h3>
                  <div className="text-3xl font-bold text-orange-600 mt-2">
                    {getTasksByStatus('pending').length}
                  </div>
                </div>
                <div className="text-orange-500 text-4xl">⏳</div>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                Tasks remaining today
              </p>
            </div>
          </div>
        )}

        {/* Weekly Progress */}
        {weeklyProgress.length > 0 && (
          <div className="mb-8">
            <TaskCalendar
              days={weeklyProgress}
              variant="week"
              onDayClick={handleDayClick}
            />
          </div>
        )}

        {/* Task Sections */}
        {schedule && schedule.sections.length > 0 ? (
          <div className="space-y-8">
            {schedule.sections.map((section, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{section.title}</h2>
                    <p className="text-gray-600 mt-1">{section.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">{section.progress}%</div>
                    <div className="text-sm text-gray-600">
                      {section.completedTasks}/{section.totalTasks} completed
                    </div>
                  </div>
                </div>

                <ProgressBar
                  progress={section.progress}
                  variant={section.progress >= 80 ? 'success' : 'default'}
                  className="mb-6"
                  animated
                />

                {section.tasks && section.tasks.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {section.tasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onClick={() => handleTaskClick(task)}
                        showTimeUntil={selectedDate === 'today'}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-400 text-4xl mb-2">📝</div>
                    <p className="text-gray-600">No tasks in this section</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📋</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks scheduled</h3>
            <p className="text-gray-600 mb-4">
              {selectedDate === 'today' 
                ? 'You have no tasks scheduled for today.' 
                : 'No tasks found for this date.'
              }
            </p>
            <Button onClick={() => setSelectedDate('today')}>
              View Today's Tasks
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// Task Details View Component
interface TaskDetailsViewProps {
  task: ScheduledTask;
  onBack: () => void;
}

const TaskDetailsView: React.FC<TaskDetailsViewProps> = ({ task, onBack }) => {
  const categoryDisplayName = tasksService.getCategoryDisplayName(task.category);
  const categoryColorClass = tasksService.getCategoryColorClass(task.category);
  const statusInfo = tasksService.getStatusDisplayInfo(task.status);
  const priorityInfo = tasksService.getPriorityDisplayInfo(task.priority);
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button onClick={onBack} variant="secondary">
              ← Back to Tasks
            </Button>
          </div>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {task.displayName || task.name}
              </h1>
              <p className="text-gray-600 mb-4">
                {task.focusedDescription ?? task.description}
              </p>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${categoryColorClass}`}>
                  {categoryDisplayName}
                </span>
                <StatusBadge status={task.status as any} size="md" />
                {task.priority !== 'regular' && (
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    task.priority === 'high' 
                      ? 'bg-red-100 text-red-800 border-red-200'
                      : 'bg-gray-100 text-gray-600 border-gray-200'
                  }`}>
                    {priorityInfo.icon} {priorityInfo.text} Priority
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Task Details */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Task Details</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-600">Scheduled Time</label>
                  <div className="text-lg font-semibold text-gray-900">
                    {tasksService.getFormattedTime(task.targetTimeHour, task.targetTimeMinute)}
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-600">Duration</label>
                  <div className="text-lg font-semibold text-gray-900">
                    {task.estimatedDuration} minutes
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-600">Status</label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={statusInfo.color}>{statusInfo.icon}</span>
                    <span className="font-semibold">{statusInfo.text}</span>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-600">Category</label>
                  <div className="text-lg font-semibold text-gray-900">
                    {categoryDisplayName}
                  </div>
                </div>
              </div>

              {task.progress !== undefined && (
                <div className="mt-6">
                  <label className="text-sm font-medium text-gray-600">Progress</label>
                  <div className="mt-2">
                    <ProgressBar
                      progress={task.progress}
                      variant={task.progress >= 100 ? 'success' : 'default'}
                      showLabel
                      size="lg"
                      animated
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Advice */}
            {task.advice && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-semibold text-blue-900 mb-2">💡 Advice</h3>
                <p className="text-blue-800">{task.advice}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <Button 
                  className="w-full" 
                  disabled={task.status === 'completed'}
                >
                  {task.status === 'completed' ? 'Task Completed' : 'Start Task'}
                </Button>
                <Button 
                  variant="secondary" 
                  className="w-full"
                >
                  Edit Task
                </Button>
                <Button 
                  variant="secondary" 
                  className="w-full"
                >
                  Reschedule
                </Button>
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Timeline</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Created:</span>
                  <span className="font-medium">
                    {new Date(task.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Updated:</span>
                  <span className="font-medium">
                    {new Date(task.updatedAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Scheduled:</span>
                  <span className="font-medium">
                    {new Date(task.scheduledDate).toLocaleDateString()}
                  </span>
                </div>
                {task.completedAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Completed:</span>
                    <span className="font-medium text-green-600">
                      {new Date(task.completedAt).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};